import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Check, Star, BadgeCheck, BarChart3, Image, Sparkles, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

const Pricing = () => {
  const navigate = useNavigate();

  const plans = [
    {
      id: 'free',
      name: 'Free',
      price: 0,
      description: 'Get started with basic visibility',
      features: [
        'Basic listing information',
        '1 photo',
        'Contact information displayed',
        'Receive reviews',
        'Basic search visibility',
      ],
      cta: 'Add Free Listing',
      popular: false,
    },
    {
      id: 'pro',
      name: 'Pro',
      price: 29,
      description: 'Enhanced visibility for growing practices',
      features: [
        'Everything in Free',
        'Up to 10 photos',
        'Verified badge after claim',
        'Enhanced profile fields',
        'Insurance & services display',
        'View analytics dashboard',
        'Priority email support',
      ],
      cta: 'Upgrade to Pro',
      popular: true,
    },
    {
      id: 'premium',
      name: 'Premium',
      price: 79,
      description: 'Maximum visibility and featured placement',
      features: [
        'Everything in Pro',
        'Unlimited photos',
        'Priority placement in search',
        'Featured on homepage',
        'Featured on city pages',
        'Promotional banner option',
        '"Accepting new patients" badge',
        'Dedicated account manager',
      ],
      cta: 'Go Premium',
      popular: false,
    },
  ];

  const featureIcons = {
    'Verified badge': BadgeCheck,
    'analytics': BarChart3,
    'photos': Image,
    'Featured': Sparkles,
    'Priority': Star,
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="pricing-page">
      {/* Hero */}
      <section className="gradient-hero py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl text-center">
          <Badge className="mb-4 bg-teal-100 text-teal-700">Pricing Plans</Badge>
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Choose the right plan for your practice
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Whether you're just getting started or ready to grow, we have a plan that fits your needs.
          </p>
        </div>
      </section>

      {/* Plans */}
      <section className="py-16 -mt-12">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-6xl">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {plans.map((plan) => (
              <div
                key={plan.id}
                className={`relative bg-white rounded-2xl border-2 p-8 transition-all hover:shadow-lg ${
                  plan.popular ? 'border-teal-700 shadow-lg scale-105' : 'border-gray-200'
                }`}
                data-testid={`plan-${plan.id}`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <Badge className="bg-teal-700 text-white px-4 py-1">Most Popular</Badge>
                  </div>
                )}

                <div className="text-center mb-8">
                  <h2 className="font-heading text-2xl font-bold text-gray-900 mb-2">{plan.name}</h2>
                  <div className="flex items-baseline justify-center gap-1 mb-2">
                    <span className="text-4xl font-bold text-gray-900">${plan.price}</span>
                    <span className="text-gray-500">/month</span>
                  </div>
                  <p className="text-gray-600 text-sm">{plan.description}</p>
                </div>

                <ul className="space-y-4 mb-8">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-teal-600 shrink-0 mt-0.5" />
                      <span className="text-gray-600">{feature}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  className={`w-full ${
                    plan.popular
                      ? 'bg-teal-700 hover:bg-teal-800 text-white'
                      : 'bg-gray-100 hover:bg-gray-200 text-gray-900'
                  }`}
                  onClick={() => navigate(plan.id === 'free' ? '/add-listing' : '/add-listing?plan=' + plan.id)}
                  data-testid={`${plan.id}-cta-btn`}
                >
                  {plan.cta}
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-white border-t">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-4xl">
          <h2 className="font-heading text-3xl font-bold text-gray-900 text-center mb-12">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            {[
              {
                q: 'Can I upgrade or downgrade my plan?',
                a: 'Yes, you can change your plan at any time. Upgrades take effect immediately, and downgrades apply at the end of your billing cycle.',
              },
              {
                q: 'How does the verification process work?',
                a: 'After claiming your listing on a Pro or Premium plan, our team verifies your ownership through email domain matching, phone verification, or manual review. This typically takes 1-2 business days.',
              },
              {
                q: 'What payment methods do you accept?',
                a: 'We accept all major credit cards through Stripe, including Visa, Mastercard, American Express, and Discover.',
              },
              {
                q: 'Is there a contract or commitment?',
                a: 'No, all plans are month-to-month. You can cancel at any time with no cancellation fees.',
              },
              {
                q: 'How does priority placement work?',
                a: 'Premium listings appear at the top of relevant search results within their city and specialty categories, giving you maximum visibility.',
              },
            ].map((faq, i) => (
              <div key={i} className="border-b border-gray-200 pb-6">
                <h3 className="font-heading font-semibold text-lg text-gray-900 mb-2">{faq.q}</h3>
                <p className="text-gray-600">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-teal-700">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-4xl text-center">
          <h2 className="font-heading text-3xl font-bold text-white mb-4">
            Ready to get started?
          </h2>
          <p className="text-teal-100 mb-8">
            Join thousands of healthcare providers on MedSearcher today.
          </p>
          <Link to="/add-listing">
            <Button size="lg" className="bg-white text-teal-700 hover:bg-gray-100 rounded-full px-8">
              Add Your Listing
              <ChevronRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Pricing;
